(function () {
  const WELLFOR_SEARCH_FIELDS = "title,product_type,variants.title,variants.sku,vendor";
  const MAX_CANDIDATES = 10;

  const COMMON_SEARCH_WORDS = new Set([
    "mirror",
    "mirrors",
    "vanity",
    "vanities",
    "cabinet",
    "cabinets",
    "medicine",
    "bathroom",
    "faucet",
    "faucets",
    "bathtub",
    "bathtubs",
    "shower",
    "showers",
    "door",
    "doors",
    "toilet",
    "toilets",
    "smart",
    "led",
    "light",
    "lights",
    "black",
    "white",
    "gold",
    "brushed",
    "nickel",
    "matte",
    "chrome",
    "wood",
    "inch",
    "in",
    "with",
    "and",
    "for"
  ]);

  function cleanQuery(value) {
    return String(value || "").trim().replace(/\s+/g, " ");
  }

  function normalizeSeparatorSpacing(value) {
    return cleanQuery(value).replace(/\s*([+._\-\/])\s*/g, "$1");
  }

  function removeSpacesOnly(value) {
    return cleanQuery(value).replace(/\s+/g, "");
  }

  function removeSkuSeparators(value) {
    return cleanQuery(value).replace(/[+._\-\/\s]+/g, "");
  }

  function hasLetters(value) {
    return /[A-Za-z]/.test(value);
  }

  function hasNumbers(value) {
    return /\d/.test(value);
  }

  function hasOnlySkuCharacters(value) {
    return /^[A-Za-z0-9+._\-\/\s]+$/.test(value);
  }

  function hasStrongSkuSeparator(value) {
    return /[+_\-\/]/.test(value);
  }

  function splitSkuParts(value) {
    return normalizeSeparatorSpacing(value)
      .split(/[\s+._\-\/]+/)
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  function tokenLooksLikeSkuPiece(token) {
    return /^[A-Za-z0-9]+$/.test(token) && hasLetters(token) && hasNumbers(token);
  }

  function containsCommonWords(value) {
    return cleanQuery(value)
      .toLowerCase()
      .split(/[\s+._\-\/]+/)
      .some(function (word) {
        return COMMON_SEARCH_WORDS.has(word);
      });
  }

  function looksLikeSku(value) {
    const query = cleanQuery(value);

    if (!query || !hasOnlySkuCharacters(query)) {
      return false;
    }

    const compact = removeSkuSeparators(query);

    if (compact.length < 4 || !hasLetters(compact) || !hasNumbers(compact)) {
      return false;
    }

    const parts = splitSkuParts(query);
    const hasSkuPiece = parts.some(tokenLooksLikeSkuPiece);
    const startsLikeSku = /^[A-Za-z]{1,12}\d/.test(compact) || /^\d{1,6}[A-Za-z]{1,12}/.test(compact);
    const hasSeparator = hasStrongSkuSeparator(query);

    /*
      Prevent normal searches like:
      "48 inch vanity"
      "black bathroom mirror"
      "20 x 30 mirror"
      from being treated as SKU.
    */
    if (containsCommonWords(query) && !hasSeparator && !hasSkuPiece && !startsLikeSku) {
      return false;
    }

    if (parts.length > 12) {
      return false;
    }

    return hasSkuPiece || startsLikeSku || hasSeparator;
  }

  function uniqueCandidates(values) {
    const seen = new Set();
    const result = [];

    values.forEach(function (value) {
      const candidate = cleanQuery(value);

      if (!candidate) {
        return;
      }

      const key = candidate.toLowerCase();

      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      result.push(candidate);
    });

    return result.slice(0, MAX_CANDIDATES);
  }

  function addPrefixFallbacks(candidates, value) {
    const compact = removeSkuSeparators(value);

    if (compact.length < 7) {
      return;
    }

    /*
      Keep this conservative.
      We do not want too many network requests or unrelated results.
    */
    const lengths = [
      compact.length - 1,
      compact.length - 2,
      compact.length - 3
    ];

    lengths.forEach(function (length) {
      if (length >= 5) {
        candidates.push(compact.slice(0, length));
      }
    });

    const familyMatch = compact.match(/^[A-Za-z]+\d?[A-Za-z]*/);

    if (familyMatch && familyMatch[0].length >= 3) {
      candidates.push(familyMatch[0]);
    }
  }

  function buildSearchCandidates(value) {
    const rawQuery = cleanQuery(value);

    if (!rawQuery) {
      return [];
    }

    if (!looksLikeSku(rawQuery)) {
      return [rawQuery];
    }

    const separatorNormalized = normalizeSeparatorSpacing(rawQuery);
    const noSpacesOnly = removeSpacesOnly(separatorNormalized);
    const candidates = [
      rawQuery,
      separatorNormalized,
      noSpacesOnly
    ];

    /*
      Important:
      If the SKU contains +, treat it as a valid SKU separator first.
      Example:
      ROSA1230MC-L+ROSA2030MC-L+ROSA1230MC-L

      We only split by + as a later fallback for predictive suggestions.
      We never submit the split version as the final search query.
    */
    if (separatorNormalized.includes("+")) {
      const bundleParts = separatorNormalized
        .split("+")
        .map(function (part) {
          return part.trim();
        })
        .filter(Boolean);

      bundleParts.forEach(function (part) {
        candidates.push(part);
        candidates.push(removeSpacesOnly(part));
      });
    } else {
      const noSeparators = removeSkuSeparators(separatorNormalized);

      if (noSeparators !== separatorNormalized && noSeparators !== noSpacesOnly) {
        candidates.push(noSeparators);
      }

      addPrefixFallbacks(candidates, separatorNormalized);
    }

    return uniqueCandidates(candidates);
  }

  function getShopifyRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }

    return "/";
  }

  function buildPredictiveSearchUrl(query, sectionId) {
    const params = new URLSearchParams();

    params.set("q", query);
    params.set("section_id", sectionId || "");
    params.set("resources[type]", "product,query,collection,article");
    params.set("resources[limit]", "10");
    params.set("resources[limit_scope]", "each");
    params.set("resources[options][unavailable_products]", "last");
    params.set("resources[options][fields]", WELLFOR_SEARCH_FIELDS);

    return `${getShopifyRoot()}search/suggest?${params.toString()}`;
  }

  function buildFallbackSearchUrl(query, sectionId) {
    const params = new URLSearchParams();

    params.set("q", query);
    params.set("section_id", sectionId || "");
    params.set("type", "product");
    params.set("options[prefix]", "last");
    params.set("options[unavailable_products]", "last");
    params.set("options[fields]", WELLFOR_SEARCH_FIELDS);

    return `${getShopifyRoot()}search?${params.toString()}`;
  }

  async function fetchHtml(url, signal) {
    const response = await fetch(url, { signal: signal });
    const html = await response.text();

    return new DOMParser().parseFromString(html, "text/html");
  }

  function hasProductResults(documentNode) {
    return Boolean(
      documentNode.querySelector('[slot="results"] [data-wellfor-product-results="true"]') ||
      documentNode.querySelector('[slot="results"] a[href*="/products/"]')
    );
  }

  function hasNoResults(documentNode) {
    const resultsSlot = documentNode.querySelector('[slot="results"]');

    if (!resultsSlot) {
      return true;
    }

    return Boolean(resultsSlot.querySelector(".empty-state"));
  }

  function replaceResults(searchElement, documentNode) {
    const newResults = documentNode.querySelector('[slot="results"]');
    const currentResults = searchElement.querySelector('[slot="results"]');

    if (newResults && currentResults) {
      currentResults.replaceWith(document.importNode(newResults, true));
    }
  }

  async function runSearchCandidates(searchElement, urlBuilder) {
    const rawQuery = cleanQuery(searchElement._queryInput && searchElement._queryInput.value);
    const sectionId = searchElement.getAttribute("section-id");
    const candidates = buildSearchCandidates(rawQuery);

    let bestDocument = null;
    let lastDocument = null;

    for (const candidate of candidates) {
      const documentNode = await fetchHtml(
        urlBuilder(candidate, sectionId),
        searchElement._abortController ? searchElement._abortController.signal : undefined
      );

      lastDocument = documentNode;

      if (hasProductResults(documentNode)) {
        bestDocument = documentNode;
        break;
      }

      if (!hasNoResults(documentNode) && !bestDocument) {
        bestDocument = documentNode;
      }
    }

    return bestDocument || lastDocument;
  }

  function normalizeQueryForFinalSubmit(value) {
    const rawQuery = cleanQuery(value);

    if (!looksLikeSku(rawQuery)) {
      return rawQuery;
    }

    /*
      Final search-page rule:
      preserve real SKU separators.

      Correct:
      OSA48CCM-CNB-BG stays OSA48CCM-CNB-BG
      ROSA1230MC-L+ROSA2030MC-L stays ROSA1230MC-L+ROSA2030MC-L

      Also correct:
      F8MCL 2430L becomes F8MCL2430L
      ROSA 4830 becomes ROSA4830
      OSA48CCM - CNB - BG becomes OSA48CCM-CNB-BG
    */
    let query = normalizeSeparatorSpacing(rawQuery);

    const parts = query.split(/\s+/).filter(Boolean);

    if (parts.length > 1) {
      const allPartsLookSkuSafe = parts.every(function (part) {
        const cleanPart = part.replace(/[+._\-\/]/g, "");

        return /^[A-Za-z0-9]+$/.test(cleanPart) && !COMMON_SEARCH_WORDS.has(cleanPart.toLowerCase());
      });

      if (allPartsLookSkuSafe) {
        query = parts.join("");
      }
    }

    return query;
  }

  function isSearchForm(form) {
    if (!form || !form.querySelector('input[name="q"]')) {
      return false;
    }

    const action = form.getAttribute("action") || "";

    return form.getAttribute("role") === "search" || action.includes("/search") || action === "";
  }

  function findInputByName(form, name) {
    return Array.from(form.querySelectorAll("input")).find(function (input) {
      return input.name === name;
    });
  }

  function ensureHiddenInput(form, name, value) {
    let input = findInputByName(form, name);

    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      form.appendChild(input);
    }

    input.value = value;
  }

  customElements.whenDefined("predictive-search").then(function () {
    const PredictiveSearch = customElements.get("predictive-search");

    if (!PredictiveSearch || PredictiveSearch.prototype.__wellforSkuSearchInstalled) {
      return;
    }

    PredictiveSearch.prototype.__wellforSkuSearchInstalled = true;

    PredictiveSearch.prototype._doPredictiveSearch = async function () {
      await this._transitionToSlot("loading");

      const documentNode = await runSearchCandidates(this, buildPredictiveSearchUrl);

      if (documentNode) {
        replaceResults(this, documentNode);
      }

      return this._transitionToSlot("results");
    };

    PredictiveSearch.prototype._doFallbackSearch = async function () {
      await this._transitionToSlot("loading");

      const documentNode = await runSearchCandidates(this, buildFallbackSearchUrl);

      if (documentNode) {
        replaceResults(this, documentNode);
      }

      return this._transitionToSlot("results");
    };
  });

  document.addEventListener(
    "submit",
    function (event) {
      const form = event.target;

      if (!isSearchForm(form)) {
        return;
      }

      const input = form.querySelector('input[name="q"]');

      if (!input) {
        return;
      }

      input.value = normalizeQueryForFinalSubmit(input.value);

      ensureHiddenInput(form, "type", "product");
      ensureHiddenInput(form, "options[prefix]", "last");
      ensureHiddenInput(form, "options[unavailable_products]", "last");
      ensureHiddenInput(form, "options[fields]", WELLFOR_SEARCH_FIELDS);
    },
    true
  );
})();